import { addItemsToCart } from '@theme/cart-add-helper';

const STORAGE_KEY = 'ccs:wishlist';

/** @returns {Array<any>} */
function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

/** @param {Array<any>} items */
function writeWishlist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    /* private mode / quota — degrade silently */
  }
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
}

/** @param {string|number} id */
function inWishlist(id) {
  return readWishlist().some((item) => String(item.id) === String(id));
}

/**
 * @param {any} product
 * @returns {boolean} true if the product was added, false if removed
 */
function toggleWishlist(product) {
  const items = readWishlist();
  const index = items.findIndex((item) => String(item.id) === String(product.id));
  if (index > -1) {
    items.splice(index, 1);
    writeWishlist(items);
    return false;
  }
  items.push(product);
  writeWishlist(items);
  return true;
}

function htmlEscape(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

function updateCounts() {
  const count = readWishlist().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = String(count);
    el.toggleAttribute('hidden', count === 0);
  });
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('[data-wishlist-label]');
    this.product = this.#parseProduct();
    if (!this.button) return;
    this.#sync();
    this.button.addEventListener('click', this.#onClick);
    document.addEventListener('wishlist:change', this.#sync);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    document.removeEventListener('wishlist:change', this.#sync);
  }

  #parseProduct() {
    try {
      return JSON.parse(this.dataset.product || '{}');
    } catch (error) {
      return {};
    }
  }

  #onClick = () => {
    if (!this.product || !this.product.id) return;
    this.#setState(toggleWishlist(this.product));
  };

  #sync = () => {
    if (!this.product || !this.product.id) return;
    this.#setState(inWishlist(this.product.id));
  };

  #setState(active) {
    this.classList.toggle('is-active', active);
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (this.label && this.dataset.labelAdded && this.dataset.labelDefault) {
      this.label.textContent = active ? this.dataset.labelAdded : this.dataset.labelDefault;
    }
  }
}

class WishlistList extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.render();
    document.addEventListener('wishlist:change', this.render);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this.render);
  }

  render = () => {
    if (!this.grid) return;
    const items = readWishlist();

    if (items.length === 0) {
      this.grid.innerHTML = '';
      this.grid.hidden = true;
      if (this.empty) this.empty.hidden = false;
      return;
    }

    if (this.empty) this.empty.hidden = true;
    this.grid.hidden = false;
    this.grid.innerHTML = items.map((item) => this.#card(item)).join('');

    this.grid.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-wishlist-remove');
        writeWishlist(readWishlist().filter((item) => String(item.id) !== String(id)));
      });
    });

    this.grid.querySelectorAll('[data-wishlist-add]').forEach((btn) => {
      btn.addEventListener('click', () => this.#addToCart(btn));
    });
  };

  #card(item) {
    const image = item.image
      ? `<img src="${htmlEscape(item.image)}" alt="${htmlEscape(item.title)}" loading="lazy" width="320" height="320">`
      : '';
    const addLabel = this.dataset.addLabel || 'Add to cart';
    const removeLabel = this.dataset.removeLabel || 'Remove';
    const addButton = item.variantId
      ? `<button type="button" class="button wishlist-card__add" data-wishlist-add data-variant-id="${htmlEscape(item.variantId)}">${htmlEscape(addLabel)}</button>`
      : '';
    return `
      <li class="wishlist-card">
        <a class="wishlist-card__media" href="${htmlEscape(item.url)}">${image}</a>
        <div class="wishlist-card__info">
          <a class="wishlist-card__title" href="${htmlEscape(item.url)}">${htmlEscape(item.title)}</a>
          <span class="wishlist-card__price">${htmlEscape(item.price)}</span>
        </div>
        <div class="wishlist-card__actions">
          ${addButton}
          <button type="button" class="wishlist-card__remove" data-wishlist-remove="${htmlEscape(item.id)}" aria-label="${htmlEscape(removeLabel)}">&times;</button>
        </div>
      </li>`;
  }

  async #addToCart(btn) {
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    btn.disabled = true;
    try {
      await addItemsToCart([{ id: variantId, quantity: 1 }], 'wishlist');
    } catch (error) {
      /* helper already surfaced the error event */
    } finally {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-list')) customElements.define('wishlist-list', WishlistList);

document.addEventListener('wishlist:change', updateCounts);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateCounts);
} else {
  updateCounts();
}
